import { AfterContentInit, Component, forwardRef, Input, ViewChild } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { Masks, Unmasks } from '../../../constants/mask.constants';
import { BaseFormControlComponent } from '../base-form-control.component';

/**
 * Currency input for Dashboard
 * @export
 * @class LsqCurrencyInputComponent
 * @extends {BaseFormControlComponent<string>}
 * @implements {AfterContentInit}
 * @property {string} name The name of input which will create a unique id
 * @property {FormControl} [control] Add the control to add unique validation messages. Either the control or group must be present for custom messages.
 * @property {FormGroup} [group] Add the group to add unique validation messages. Either the control or group must be present for custom messages.
 * @property {string} [placeholder] Adds a placeholder to the input
 * @property {string} [inputClass] Adds to the `class` on the currency input.
 * @property {string} [parentClass] Adds to the `class` on the input-group.
 * @property {string} [readonly] Set's the input to readonly
 * @todo Custom Validation Directive to add custom validation messages
 */
@Component({
    selector: 'lsq-currency-input',
    template: `
        <lsq-input
            #coreInput
            [prependIcon]="[icons.Dollar, color.Green]"
            [name]="name"
            [label]="label"
            [group]="group"
            [floatLabel]="floatLabel"
            [innerLabel]="innerLabel"
            [basicLabel]="basicLabel"
            [type]="type"
            [min]="min"
            [max]="max"
            [placeholder]="placeholder"
            [readOnly]="readOnly"
            [parentClass]="parentClass"
            [inputGroupClass]="inputGroupClassName"
            [inputClass]="inputClass"
            [textFormatter]="formatText"
            [handleKeypress]="handleKeypress"
            [autoFocus]="autoFocus"
            [tabindex]="tabindex"
            [mask]="currencyMask"
            [control]="getControl()"
        >
        </lsq-input>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => LsqCurrencyInputComponent),
            multi: true
        }
    ]
})
export class LsqCurrencyInputComponent implements AfterContentInit, ControlValueAccessor {
    @ViewChild('coreInput', { static: true }) public input: BaseFormControlComponent<string>;
    @Input() public name: string;
    @Input() public label: string;
    @Input() public group?: FormGroup;
    @Input() public control?: FormControl;
    @Input() public formControlName: string;
    @Input() public floatLabel? = false;
    @Input() public innerLabel? = true;
    @Input() public basicLabel? = false;
    @Input() public type? = 'text';
    @Input() public placeholder?: string;
    @Input() public min?: number;
    @Input() public max?: number;
    @Input() public readOnly? = false;
    @Input() public parentClass?: string;
    @Input() public inputClass?: string;
    @Input() public inputGroupClass?: string;
    @Input() public labelClass?: string;
    @Input() public autoFocus? = false;
    @Input() public tabindex?: string;
    @Input() public currencyMask? = Masks.CurrencyMask;

    public icons = Icons;
    public color = Color;

    public ngAfterContentInit(): void {
        this.inputClass = this.inputClass ? `form-control ${this.inputClass}` : 'form-control';
    }

    public formatText(value: any): any {
        // Remove commas
        if (!value && value !== '') {
            return value;
        }
        let formattedValue = value.toString().replace(Unmasks.CurrencyUnmask, '');

        if (value !== null && value !== '' && !isNaN(formattedValue)) {
            formattedValue = (+formattedValue).toFixed(2);
        }
        return formattedValue;
    }

    public handleKeypress(event: KeyboardEvent): void {
        const pattern = /^-?[0-9\.-]/;
        const inputChar = String.fromCharCode(event.charCode);

        // check if non-numeric char
        if (!pattern.test(inputChar)) {
            // invalid character, prevent input
            event.preventDefault();
        }
    }

    public getControl(): AbstractControl | null {
        if (this.control) {
            return this.control;
        } else if (this.group) {
            return this.group.get(this.formControlName);
        }
        return null;
    }

    public get inputGroupClassName(): string {
        let className = 'currency-input-group';
        if (!!this.inputGroupClass) {
            className = `${className} ${this.inputGroupClass}`;
        }
        return className;
    }

    // Jumper these ControlValueAccessor functions to the underlying lsq-input
    public writeValue(model: string): void {
        this.input.writeValue(model);
    }
    public registerOnChange(fn: (_: string) => void): void {
        this.input.registerOnChange((value: string) => {
            // Strip out commas. When using an `unmask` the first keypress of decimal char was getting dropped.
            fn(value ? value.replace(Unmasks.CurrencyUnmask, '') : value);
        });
    }
    public registerOnTouched(fn: () => void): void {
        this.input.registerOnTouched(fn);
    }

    // Called directly from FormGroup
    public setDisabledState(isDisabled: boolean): void {
        this.input.disabled = isDisabled;
    }
}
